import React, { useEffect, useState } from 'react';
import { apiService, PenaltyV2DebugResponse } from '../services/api';
import { formatDistanceToNow, parseISO } from 'date-fns';

interface PenaltyV2DebugCardProps {
  sku: string;
}

const PenaltyV2DebugCard: React.FC<PenaltyV2DebugCardProps> = ({ sku }) => {
  const [data, setData] = useState<PenaltyV2DebugResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [showConfig, setShowConfig] = useState<boolean>(false);

  useEffect(() => {
    const fetchPenaltyDebug = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getProductPenaltyDebug(sku);
        setData(response);
      } catch (err: any) {
        console.error('Error fetching penalty debug:', err);
        setError(err.response?.data?.error || 'Erro ao carregar dados de penalização');
      } finally {
        setLoading(false);
      }
    };

    if (sku) {
      fetchPenaltyDebug();
    }
  }, [sku]);

  if (loading) {
    return (
      <div className="card shadow-sm">
        <div className="card-body text-center">
          <div className="spinner-border spinner-border-sm text-primary" role="status">
            <span className="visually-hidden">A carregar...</span>
          </div>
          <p className="text-muted mt-2 mb-0 small">A carregar dados de penalização V2.0...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="card shadow-sm border-warning">
        <div className="card-body">
          <div className="d-flex align-items-center">
            <i className="bi bi-exclamation-triangle-fill text-warning me-2 fs-5"></i>
            <div>
              <h6 className="mb-1">Penalty V2.0 Debug</h6>
              <p className="text-muted mb-0 small">{error}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { product_info, penalty_calculation, stored_values, comparison } = data;
  const { debug_info } = penalty_calculation;

  // Calculate penalty percentage (0-100%)
  const penaltyPercentage = Math.round(penalty_calculation.penalty_factor * 100);

  // Determine color based on penalty severity
  const getPenaltyColor = (factor: number): string => {
    if (factor >= 0.9) return 'success';
    if (factor >= 0.7) return 'warning';
    if (factor >= 0.5) return 'orange';
    return 'danger';
  };

  // Get category badge color
  const getCategoryBadgeClass = (category: string): string => {
    switch (category.toLowerCase()) {
      case 'ultra-fast':
        return 'bg-danger';
      case 'fast':
        return 'bg-warning';
      case 'medium':
        return 'bg-info';
      case 'slow':
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  };

  const penaltyColor = getPenaltyColor(penalty_calculation.penalty_factor);

  return (
    <div className="card shadow-sm">
      <div className="card-header bg-light d-flex justify-content-between align-items-center">
        <h6 className="mb-0">
          <i className="bi bi-speedometer2 me-2"></i>
          Penalty V2.0 Debug
        </h6>
        <span className={`badge ${getCategoryBadgeClass(debug_info.category)} text-uppercase`}>
          {debug_info.category}
        </span>
      </div>

      <div className="card-body">
        {/* Penalty Factor Gauge */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="text-muted small">Penalty Factor</span>
            <span className={`fw-bold fs-5 text-${penaltyColor}`}>
              {penaltyPercentage}%
            </span>
          </div>
          <div className="progress" style={{ height: '20px' }}>
            <div
              className={`progress-bar bg-${penaltyColor}`}
              role="progressbar"
              style={{ width: `${penaltyPercentage}%` }}
              aria-valuenow={penaltyPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              {penalty_calculation.penalty_factor.toFixed(3)}
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="row g-2 mb-3">
          <div className="col-6">
            <div className="p-2 bg-light rounded">
              <div className="text-muted small">DPD_7</div>
              <div className="fw-bold">{debug_info.dpd_7?.toFixed(2) ?? 'N/A'} units/day</div>
            </div>
          </div>
          <div className="col-6">
            <div className="p-2 bg-light rounded">
              <div className="text-muted small">Horas Ativas Overdue</div>
              <div className="fw-bold">{debug_info.active_hours_overdue?.toFixed(1) ?? 'N/A'}h</div>
            </div>
          </div>
          <div className="col-6">
            <div className="p-2 bg-light rounded">
              <div className="text-muted small">Última Venda</div>
              <div className="fw-bold small">
                {product_info.last_sale_timestamp
                  ? formatDistanceToNow(parseISO(product_info.last_sale_timestamp), {
                      addSuffix: true
                    })
                  : 'N/A'
                }
              </div>
            </div>
          </div>
          <div className="col-6">
            <div className="p-2 bg-light rounded">
              <div className="text-muted small">Horas Totais</div>
              <div className="fw-bold">{debug_info.hours_since_last_sale?.toFixed(1) ?? 'N/A'}h</div>
            </div>
          </div>
        </div>

        {/* Penalty Explanation */}
        <div className="alert alert-info small mb-3">
          <i className="bi bi-info-circle me-2"></i>
          {penalty_calculation.penalty_note}
        </div>

        {/* Comparison with Stored Values */}
        {!comparison.calculation_matches_stored && (
          <div className="alert alert-warning small mb-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Aviso:</strong> Valores calculados diferem dos armazenados no ranking!
          </div>
        )}

        {/* Config Toggle */}
        <div className="d-grid">
          <button
            className="btn btn-sm btn-outline-secondary"
            onClick={() => setShowConfig(!showConfig)}
          >
            {showConfig ? (
              <>
                <i className="bi bi-chevron-up me-1"></i>
                Esconder Configuração
              </>
            ) : (
              <>
                <i className="bi bi-chevron-down me-1"></i>
                Mostrar Configuração
              </>
            )}
          </button>
        </div>

        {/* Config Details (Collapsible) */}
        {showConfig && (
          <div className="mt-3 p-3 bg-light rounded small">
            <h6 className="text-muted mb-2">Configuração Usada</h6>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Decay Rate:</td>
                  <td className="text-end fw-bold">{penalty_calculation.config_used.decay_rate}</td>
                </tr>
                <tr>
                  <td className="text-muted">Min Penalty:</td>
                  <td className="text-end fw-bold">{Math.round(penalty_calculation.config_used.min_penalty * 100)}%</td>
                </tr>
                {penalty_calculation.config_used.dpd_threshold_for_next && (
                  <tr>
                    <td className="text-muted">Next Threshold:</td>
                    <td className="text-end fw-bold">DPD ≥ {penalty_calculation.config_used.dpd_threshold_for_next}</td>
                  </tr>
                )}
              </tbody>
            </table>

            <hr className="my-2" />

            <h6 className="text-muted mb-2">Valores Armazenados</h6>
            <table className="table table-sm table-borderless mb-0">
              <tbody>
                <tr>
                  <td className="text-muted">Penalty Factor:</td>
                  <td className="text-end fw-bold">
                    {stored_values.penalty_v2_factor?.toFixed(3) || 'N/A'}
                    {comparison.calculation_matches_stored ? (
                      <i className="bi bi-check-circle-fill text-success ms-1"></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-danger ms-1"></i>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Category:</td>
                  <td className="text-end">
                    <span className={`badge ${getCategoryBadgeClass(stored_values.penalty_v2_category || '')}`}>
                      {stored_values.penalty_v2_category || 'N/A'}
                    </span>
                    {comparison.category_matches_stored ? (
                      <i className="bi bi-check-circle-fill text-success ms-1"></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-danger ms-1"></i>
                    )}
                  </td>
                </tr>
                <tr>
                  <td className="text-muted">Active Hours Overdue:</td>
                  <td className="text-end fw-bold">
                    {stored_values.active_hours_overdue?.toFixed(1) || 'N/A'}h
                    {comparison.overdue_matches_stored ? (
                      <i className="bi bi-check-circle-fill text-success ms-1"></i>
                    ) : (
                      <i className="bi bi-x-circle-fill text-danger ms-1"></i>
                    )}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default PenaltyV2DebugCard;
